import React, { useMemo } from 'react';
import { Check, Loader2 } from 'lucide-react';
import SettingsSectionHeader from './SettingsSectionHeader';
import { Card, CardContent } from '../ui/card';
import {
  PERMISSION_GROUPS,
  parseRolePermissions,
  permissionLabel,
  filterSchoolSettingsRoles,
  isRolePermissionEditable,
} from '../../utils/rolePermissions';
import { cn } from '../../lib/utils';

function SettingsCard({ children }) {
  return <Card className="rounded-2xl border-border shadow-sm">{children}</Card>;
}

function PermissionCell({ active, editable, saving, onToggle }) {
  if (saving) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      </span>
    );
  }

  if (!editable) {
    return active ? (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-500/15 text-green-600 dark:text-green-400">
        <Check className="h-3.5 w-3.5" />
      </span>
    ) : (
      <span className="inline-block h-7 w-7 rounded-full bg-muted/50" />
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-full border transition-all',
        active
          ? 'bg-green-500/15 border-green-500/40 text-green-600 dark:text-green-400 hover:bg-green-500/25'
          : 'bg-muted/40 border-border hover:border-blue-400 hover:bg-blue-500/10'
      )}
      title={active ? 'Click to revoke' : 'Click to grant'}
    >
      {active ? <Check className="h-3.5 w-3.5" /> : null}
    </button>
  );
}

export default function PermissionsSection({
  roles = [],
  canEdit = false,
  savingKey = '',
  onTogglePermission,
}) {
  const normalizedRoles = useMemo(
    () =>
      filterSchoolSettingsRoles(roles).map((r) => ({
        ...r,
        permissions: parseRolePermissions(r),
      })),
    [roles]
  );

  const roleHasPermission = (role, key) => role.permissions.includes(key);

  return (
    <>
      <SettingsSectionHeader
        sectionId="roles.permissions"
        description={
          canEdit
            ? 'Click cells to grant or revoke permissions. Changes save immediately.'
            : 'Permission matrix — view only for your role.'
        }
      />
      <SettingsCard>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left font-semibold text-foreground p-3 sticky left-0 bg-muted/40 z-10 min-w-[180px]">
                  Permission
                </th>
                {normalizedRoles.map((role) => (
                  <th key={role.id} className="text-center font-medium text-muted-foreground p-3 min-w-[88px]">
                    <span className="block text-xs truncate max-w-[88px]" title={role.name}>
                      {role.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_GROUPS.map((group) => (
                <React.Fragment key={group.label}>
                  <tr className="bg-muted/20">
                    <td
                      colSpan={normalizedRoles.length + 1}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"
                    >
                      {group.label}
                    </td>
                  </tr>
                  {group.keys.map((key) => (
                    <tr key={key} className="border-b border-border/60 hover:bg-muted/10">
                      <td className="p-3 sticky left-0 bg-card font-medium text-foreground">
                        {permissionLabel(key)}
                      </td>
                      {normalizedRoles.map((role) => {
                        const active = roleHasPermission(role, key);
                        const editable = canEdit && isRolePermissionEditable(role);
                        const cellKey = `${role.id}:${key}`;
                        return (
                          <td key={role.id} className="p-3 text-center">
                            <PermissionCell
                              active={active}
                              editable={editable}
                              saving={savingKey === cellKey}
                              onToggle={() => editable && onTogglePermission?.(role, key, !active)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </SettingsCard>
    </>
  );
}
